import { Map as ImmutableMap, fromJS } from 'immutable';
import http from 'hub-http/clients/apiClient';
import formatVisitorData from 'documents-ui/utils/formatVisitorData';
import { fetchWithEarlyRequestHook } from './quickFetchHelper';
import DocumentMetadataRecord from '../records/DocumentMetadataRecord';
import { createRpcClientV2, makeChirpEarlyRequest } from 'rpc-client-utils';
import { shareDocumentRpc, updateDocumentRpc, moveDocumentsRpc, batchDeleteDocumentsRpc, getDocumentRpc, getDocumentUsageRpc } from '../../__generated__/chirp/com/hubspot/sales/documents/rpc/SalesDocumentsDocumentService';
import { getDocumentPdfDownloadUrlEarlyRequestRpc, getDocumentEarlyRequestRpc, getDocumentDownloadUrlEarlyRequestRpc } from '../../__generated__/chirp/com/hubspot/sales/documents/rpc/earlyRequest/SalesDocumentsDocumentService.early';
const baseUrl = 'presentations/v1/deck';
const baseAnalyticsUrl = 'presentations/v1/analytics/deck';

// Note: This value is also hardcoded in pre-fetch script (/static/html/index.html.js)
export const VISITORS_PAGE_LIMIT = 10;
export function fetchDocument(id) {
  return http.get(`${baseUrl}/${id}`).then(fromJS);
}
export function fetchDocumentCHIRPEarlyRequest(id) {
  return makeChirpEarlyRequest(getDocumentEarlyRequestRpc, {
    request: {
      documentId: Number(id)
    }
  });
}
export async function fetchDocumentCHIRP(id) {
  const rpcClient = createRpcClientV2({
    hubHttpClient: http
  });
  return await rpcClient.call(getDocumentRpc, {
    request: {
      documentId: Number(id)
    }
  });
}
export function updateDocument({
  documentId,
  folderId,
  title
}) {
  return http.put(`${baseUrl}/${documentId}`, {
    data: {
      title,
      folderId
    }
  }).then(fromJS);
}
export async function updateDocumentCHIRP({
  documentId,
  title
}) {
  const rpcClient = createRpcClientV2({
    hubHttpClient: http
  });
  return await rpcClient.call(updateDocumentRpc, {
    request: {
      documentId,
      title
    }
  });
}
export function updateDocumentBrand({
  documentId,
  brandId
}) {
  return http.put(`presentations/v1/deckBrand/${documentId}/updateBrand`, {
    data: {
      brandId
    }
  });
}
export function batchUpdateDocumentsBrand({
  documentIds,
  brandId
}) {
  return http.put('presentations/v1/deckBrand/batch/updateBrand', {
    data: {
      deckIds: documentIds,
      brandId
    }
  });
}
export function moveDocuments(sequenceIds, folderId) {
  return http.put(`${baseUrl}/move-to-folder/batch`, {
    data: {
      deckIds: sequenceIds.toArray(),
      destinationFolderId: folderId
    }
  }).then(fromJS);
}
export function moveDocumentsCHIRP(sequenceIds, folderId) {
  const rpcClient = createRpcClientV2({
    hubHttpClient: http
  });
  return rpcClient.call(moveDocumentsRpc, {
    request: {
      documentIds: sequenceIds.toArray(),
      destinationFolderId: folderId !== null && folderId !== void 0 ? folderId : undefined
    }
  });
}
export function removeDocuments(documentIds) {
  return http.delete(`${baseUrl}/batch`, {
    data: {
      ids: documentIds
    }
  }).then(fromJS);
}
export function removeDocumentsCHIRP(documentIds) {
  const rpcClient = createRpcClientV2({
    hubHttpClient: http
  });
  return rpcClient.call(batchDeleteDocumentsRpc, {
    request: {
      documentIds
    }
  });
}
export function shareDocument({
  id,
  email,
  requireEmail
}) {
  return http.post(`${baseUrl}/${id}/share`, {
    query: {
      sharedWith: email,
      skipForm: !requireEmail
    }
  }).then(fromJS);
}
export function shareDocumentCHIRP({
  id,
  name,
  email,
  requireEmail
}) {
  const rpcClient = createRpcClientV2({
    hubHttpClient: http
  });
  return rpcClient.call(shareDocumentRpc, {
    request: {
      documentId: id,
      name,
      sharedWithEmail: email,
      skipForm: !requireEmail
    }
  });
}
export const fetchDocumentsUsage = () => http.get('presentations/v1/deck/usage').then(fromJS);
export const fetchDocumentsUsageCHIRP = () => {
  const rpcClient = createRpcClientV2({
    hubHttpClient: http
  });
  return rpcClient.call(getDocumentUsageRpc, {});
};
export function fetchDownloadLink(id) {
  return http.get(`${baseUrl}/${id}/downloadUrl`).then(fromJS);
}
export const fetchDownloadLinkCHIRP = id => makeChirpEarlyRequest(getDocumentDownloadUrlEarlyRequestRpc, {
  request: {
    documentId: Number(id)
  }
});
export function fetchPresentationPdfUrl(id) {
  return http.get(`${baseUrl}/${id}/pdfDownloadUrl`).then(fromJS);
}
export const fetchPresentationPdfUrlCHIRP = id => {
  return makeChirpEarlyRequest(getDocumentPdfDownloadUrlEarlyRequestRpc, {
    request: {
      documentId: Number(id)
    }
  });
};
export function fetchDocumentMetadata(deckId) {
  const fallbackFetch = () => http.get(`${baseAnalyticsUrl}/${deckId}/summary`);
  const successHandler = documentMetadataJS => {
    var _deck$get;
    const documentMetadata = fromJS(documentMetadataJS);
    const {
      deck,
      deckAnalytics
    } = documentMetadata.toObject();
    const userId = deck.get('userId');
    const brandId = (_deck$get = deck.get('brandId')) !== null && _deck$get !== void 0 ? _deck$get : 0;
    const slides = deck.get('slides').reduce((slidesMap, slide) => slidesMap.set(slide.get('id'), slide), ImmutableMap());
    return DocumentMetadataRecord(deckAnalytics.set('slides', slides).set('userId', userId).set('brandId', brandId));
  };
  return fetchWithEarlyRequestHook('document-metadata', fallbackFetch, successHandler);
}
export function fetchDocumentVisitors({
  deckId,
  offset,
  limit = VISITORS_PAGE_LIMIT
}) {
  const fallbackFetch = () => http.get(`${baseAnalyticsUrl}/${deckId}/visitors`, {
    query: {
      limit,
      offset
    }
  });
  const successHandler = response => formatVisitorData(fromJS(response));
  if (!offset) {
    return fetchWithEarlyRequestHook('document-visitors', fallbackFetch, successHandler);
  }
  return fallbackFetch().then(successHandler);
}
export function searchDocumentVisitors({
  deckId,
  offset,
  documentVisitorSearchQuery,
  limit = VISITORS_PAGE_LIMIT
}) {
  return http.get(`${baseAnalyticsUrl}/${deckId}/visitors/search`, {
    query: {
      limit,
      offset,
      query: documentVisitorSearchQuery
    },
    timeout: 10000
  }).then(fromJS).then(formatVisitorData);
}
export function fetchDocumentRevisits({
  deckId,
  vid
}) {
  return http.get(`${baseAnalyticsUrl}/${deckId}/visitors/${vid}`).then(fromJS);
}